import { useMsal } from '@azure/msal-react';
import { useCobaltLayoutContext } from '@cae/cobalt-react/components/layout';
import { useHeap } from '@configs/heap';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useOrganizations } from '@contexts/organizations-provider';
import { usePermissions } from '@contexts/permissions-provider';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import { useGetToken } from '@shared/hooks/use-get-token';
import * as React from 'react';

import { ReservationRequestsRouterProps } from './ReservationRequestsRouterPage.types';

/**
 * ReservationRequestsRouterPage - MFE wrapper for Reservation Requests
 *
 * This page loads the remote ReservationRequestsRouter MFE and passes
 * the required props. The MFE handles its own internal routing.
 *
 * Guard stack (applied in manage.routes.tsx):
 * 1. AuthenticatedRoute - User must be logged in
 * 2. OrgRouteGuard - Validate org access
 * 3. FeatureFlagGuard - bookingRequestPage must be enabled
 * 4. PermissionGuard - ReservationRequests_View_Org required
 *
 * @see docs/ROUTE_ARCHITECTURE.md for guard documentation
 */
const ReservationRequestsRouterPage = React.memo(
  (): React.ReactElement | null => {
    const { instance } = useMsal();
    const authData = useAuthenticatedUser();
    const { selectedOrganization } = useOrganizations();
    const { permissions } = usePermissions();
    const getToken = useGetToken();
    const heapIdentity = useHeap();

    const [RemoteRouter, setRemoteRouter] =
      React.useState<React.ComponentType<ReservationRequestsRouterProps> | null>(
        null
      );
    const [loadError, setLoadError] = React.useState<Error | null>(null);

    const { hideSideNavigation } = useCobaltLayoutContext();

    React.useEffect(() => {
      const loadRouter = async (): Promise<void> => {
        try {
          const { default: Router } =
            await import('reservations_mfe/ReservationRequestsRouter');
          setRemoteRouter(() => Router);
        } catch (error) {
          console.error('Error loading ReservationRequestsRouter:', error);
          setLoadError(error as Error);
        }
      };

      loadRouter();
    }, []);

    if (loadError) {
      return <InlineFeedback type="internal-server-error" showGoHome={false} />;
    }

    if (!RemoteRouter || !selectedOrganization || !heapIdentity) {
      return <ContentLoader />;
    }

    return (
      <React.Suspense fallback={<ContentLoader />}>
        <RemoteRouter
          msalInstance={instance}
          user={authData}
          customerId={selectedOrganization.customerId}
          permissions={permissions}
          heapIdentity={heapIdentity}
          hideSideNavigation={hideSideNavigation}
          getToken={getToken}
        />
      </React.Suspense>
    );
  }
);

ReservationRequestsRouterPage.displayName = 'ReservationRequestsRouterPage';

export { ReservationRequestsRouterPage };
